import { EventNames, globalEventBus } from '@littlebird/event-bus';
import { makeAutoObservable, observable, reaction, runInAction } from 'mobx';

import { backendApis } from '@/backendApis';
import {
  type ChatCostSummary,
  type CostBreakdown,
  type CostStepInfo,
  type TurnCostSummary,
  ZERO_COST_BREAKDOWN,
  sumBilledTokens,
  sumCostBreakdowns,
} from '@/features/chat-debug/chat-debug.types';
import { littlebirdSdk } from '@/lib/sdk-init';

export class ChatCostLiveStore {
  activeChatId: string | null = null;
  summary: ChatCostSummary | null = null;
  latestStep: CostStepInfo | null = null;

  private seedController: AbortController | null = null;
  // Each chat the threads store held while it was the active chat
  private heldChatIds = new Set<string>();
  private liveSteps = new Map<string, CostStepInfo>();
  private chatCache = new Map<
    string,
    {
      summary: ChatCostSummary | null;
      latestStep: CostStepInfo | null;
      liveSteps: Map<string, CostStepInfo>;
    }
  >();

  constructor() {
    makeAutoObservable<this, 'seedController' | 'liveSteps' | 'chatCache'>(
      this,
      {
        summary: observable.ref,
        latestStep: observable.ref,
        seedController: false,
        liveSteps: false,
        chatCache: false,
      }
    );

    globalEventBus.on(EventNames.CHAT_COST_STEP, (event) => {
      this.applyStep(event.data);
    });
    globalEventBus.on(EventNames.WS_RECONNECTED, () => {
      const chatId = this.serverChatId;
      if (chatId) void this.refetch(chatId);
    });
    // Remembers the active chat once the threads store holds it
    reaction(
      () => {
        const chatId = this.activeChatId;
        return chatId !== null && littlebirdSdk.stores.threads.get(chatId)
          ? chatId
          : null;
      },
      (chatId) => {
        if (chatId) this.heldChatIds.add(chatId);
      },
      { fireImmediately: true }
    );
    // The costs load when the active chat opens on the server, or when the
    // first send of a chat made on the device creates it there
    reaction(
      () => this.serverChatId,
      (chatId) => {
        if (chatId) void this.refetch(chatId);
      }
    );
  }

  // The active chat, once the server has it. The server answers 404 for the
  // costs of a chat it does not have:
  // - a chat made on the device, until its first send creates it there
  // - a chat that left the threads store, such as an unsent context chat
  //   that the device dropped when the user moved to another page
  // A chat the store never held was opened from the server
  get serverChatId(): string | null {
    const chatId = this.activeChatId;
    if (chatId === null) return null;
    const thread = littlebirdSdk.stores.threads.get(chatId);
    if (thread) return thread.synced ? chatId : null;
    return this.heldChatIds.has(chatId) ? null : chatId;
  }

  setActiveChatId(chatId: string | null): void {
    if (this.activeChatId === chatId) return;
    this.seedController?.abort();
    this.seedController = null;
    if (this.activeChatId) {
      this.chatCache.set(this.activeChatId, {
        summary: this.summary,
        latestStep: this.latestStep,
        liveSteps: new Map(this.liveSteps),
      });
    }
    this.activeChatId = chatId;
    if (!chatId) {
      this.summary = null;
      this.latestStep = null;
      this.liveSteps.clear();
      return;
    }
    const cached = this.chatCache.get(chatId);
    if (cached) {
      this.summary = cached.summary;
      this.latestStep = cached.latestStep;
      this.liveSteps = new Map(cached.liveSteps);
    } else {
      this.summary = null;
      this.latestStep = null;
      this.liveSteps.clear();
    }
  }

  private applyStep(step: CostStepInfo): void {
    if (!this.activeChatId || step.chat_id !== this.activeChatId) return;
    this.liveSteps.set(stepKey(step), step);
    const base = this.summary ?? emptySummary(step.chat_id);
    this.summary = mergeStepIntoSummary(base, step);
    this.latestStep = this.summary.latest_step;
    this.chatCache.set(this.activeChatId, {
      summary: this.summary,
      latestStep: this.latestStep,
      liveSteps: new Map(this.liveSteps),
    });
  }

  private async refetch(chatId: string): Promise<void> {
    this.seedController?.abort();
    const controller = new AbortController();
    this.seedController = controller;
    try {
      const summary = await backendApis.chat.getCost(chatId, controller.signal);
      const liveSteps =
        this.activeChatId === chatId
          ? this.liveSteps
          : new Map(this.chatCache.get(chatId)?.liveSteps ?? []);
      runInAction(() => {
        let nextSummary = summary;
        for (const step of liveSteps.values()) {
          nextSummary = mergeStepIntoSummary(nextSummary, step);
        }
        this.chatCache.set(chatId, {
          summary: nextSummary,
          latestStep: nextSummary.latest_step,
          liveSteps: new Map(liveSteps),
        });
        if (this.activeChatId === chatId) {
          this.summary = nextSummary;
          this.latestStep = nextSummary.latest_step;
        }
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'CanceledError') return;
      console.warn('[cost-footer] seed failed', error);
    } finally {
      if (this.seedController === controller) this.seedController = null;
    }
  }
}

function stepKey(step: CostStepInfo): string {
  if (step.message_id !== null && step.step_index !== null) {
    return `turn:${step.message_id}:${step.step_index}`;
  }
  return `compaction:${step.server_emitted_at}`;
}

function emptySummary(chatId: string): ChatCostSummary {
  return {
    chat_id: chatId,
    title: '',
    turns: [],
    total_tokens: 0,
    total_cost_usd: 0,
    compaction_calls: [],
    compaction_total: null,
    mid_turn_compaction_calls: [],
    mid_turn_compaction_total: null,
    grand_total_usd: 0,
    latest_step: null,
  };
}

function mergeStepIntoSummary(
  summary: ChatCostSummary,
  step: CostStepInfo
): ChatCostSummary {
  const nextSummary =
    step.message_id === null || step.step_index === null
      ? mergeCompactionStep(summary, step)
      : mergeTurnStep(summary, step, step.message_id, step.step_index);
  return {
    ...nextSummary,
    latest_step: newerStep(nextSummary.latest_step, step),
  };
}

function newerStep(
  current: CostStepInfo | null,
  candidate: CostStepInfo
): CostStepInfo {
  if (!current) return candidate;
  return stepTimestamp(candidate) >= stepTimestamp(current)
    ? candidate
    : current;
}

function stepTimestamp(step: CostStepInfo): number {
  const timestamp = Date.parse(step.server_emitted_at);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function stepBreakdownFromInfo(step: CostStepInfo): CostBreakdown {
  return {
    fresh_input_tokens: step.fresh_input_tokens,
    cached_read_tokens: step.cached_read_tokens,
    cache_write_tokens: step.cache_write_tokens,
    output_tokens: step.output_tokens,
    thought_tokens: step.thought_tokens,
    fresh_input_cost_usd: step.fresh_input_cost_usd,
    cached_read_cost_usd: step.cached_read_cost_usd,
    cache_write_cost_usd: step.cache_write_cost_usd,
    output_cost_usd: step.output_cost_usd,
    thought_cost_usd: step.thought_cost_usd,
    total_usd: step.total_usd,
  };
}

function mergeTurnStep(
  summary: ChatCostSummary,
  step: CostStepInfo,
  messageId: string,
  stepIndex: number
): ChatCostSummary {
  const stepBreakdown = stepBreakdownFromInfo(step);
  const turnIdx = summary.turns.findIndex((t) => t.message_id === messageId);
  let newTurns: TurnCostSummary[];
  if (turnIdx < 0) {
    const newTurn: TurnCostSummary = {
      turn_index: summary.turns.length,
      message_id: messageId,
      created_at: step.server_emitted_at,
      model: step.model,
      steps: [],
      step_costs: [stepBreakdown],
      total_cost: stepBreakdown,
      cache_expired: null,
      turn_gap_ms: null,
      subagent_runs: [],
      subagent_total: null,
    };
    newTurns = [...summary.turns, newTurn];
  } else {
    const existing = summary.turns[turnIdx];
    const newStepCosts = [...existing.step_costs];
    while (newStepCosts.length <= stepIndex) {
      newStepCosts.push(ZERO_COST_BREAKDOWN);
    }
    newStepCosts[stepIndex] = stepBreakdown;
    // Subagent rollup arrives only on seed/refetch — preserve it across live step merges.
    const ownTotal = newStepCosts.reduce(
      (acc, b) => sumCostBreakdowns(acc, b),
      ZERO_COST_BREAKDOWN
    );
    const newTotalCost = existing.subagent_total
      ? sumCostBreakdowns(ownTotal, existing.subagent_total)
      : ownTotal;
    const newTurn: TurnCostSummary = {
      ...existing,
      model: step.model,
      step_costs: newStepCosts,
      total_cost: newTotalCost,
    };
    newTurns = [...summary.turns];
    newTurns[turnIdx] = newTurn;
  }
  return recomputeTotals({ ...summary, turns: newTurns });
}

function mergeCompactionStep(
  summary: ChatCostSummary,
  step: CostStepInfo
): ChatCostSummary {
  const stepBreakdown = stepBreakdownFromInfo(step);
  const newCall: ChatCostSummary['compaction_calls'][number] = {
    created_at: step.server_emitted_at,
    model: step.model,
    usage: {
      model: step.model,
      input_tokens: step.total_prompt_tokens,
      output_tokens: step.output_tokens,
      cached_tokens: 0,
      cache_creation_tokens: 0,
      thought_tokens: step.thought_tokens,
    },
    cost: stepBreakdown,
  };
  const existingIdx = summary.compaction_calls.findIndex(
    (c) => c.created_at === step.server_emitted_at
  );
  const newCalls = [...summary.compaction_calls];
  if (existingIdx >= 0) {
    newCalls[existingIdx] = newCall;
  } else {
    newCalls.push(newCall);
  }
  return recomputeTotals({ ...summary, compaction_calls: newCalls });
}

function recomputeTotals(summary: ChatCostSummary): ChatCostSummary {
  const totalCostUsd = summary.turns.reduce(
    (acc, t) => acc + t.total_cost.total_usd,
    0
  );
  const compactionTotal: CostBreakdown =
    summary.compaction_calls.length > 0
      ? summary.compaction_calls.reduce(
          (acc, c) => sumCostBreakdowns(acc, c.cost),
          ZERO_COST_BREAKDOWN
        )
      : ZERO_COST_BREAKDOWN;
  const totalTokens = summary.turns.reduce(
    (acc, t) => acc + sumBilledTokens(t.total_cost),
    0
  );
  return {
    ...summary,
    total_tokens: totalTokens,
    total_cost_usd: totalCostUsd,
    compaction_total: compactionTotal,
    grand_total_usd: totalCostUsd + compactionTotal.total_usd,
  };
}

export const chatCostLiveStore = new ChatCostLiveStore();
